import { Component, inject } from "@angular/core";
import { MoiExportConfigModel } from "@app/export/shared/models/moi-export-config-model";
import { MoiDataService } from "@app/moi/services/moi-data.service";
import {
    GenericListField,
    GenericListFieldType,
} from "@app/shared/models/GenericList/GenericListField";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { nameof } from "@methods/jeffs-toolkit";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { map } from "rxjs/operators";
import { ExportBase } from "../export-base";
import { ExportType } from "./../../../../common/enums/ExportType";
import { IdNameCombination } from "./../../../../common/models/generic/IdNameCombination";
import { injectTranslation, TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/export/i18n/scope";

@Component({
    selector: "export-bim",
    templateUrl: "./export-bim.component.html",
    styleUrls: ["./export-bim.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [TranslatePipe]
})
export class ExportBimComponent extends ExportBase {
    private _ds = inject(MoiDataService);
    permService = inject(PermissionsService);
    private cerrixPrompt = inject(CerrixPromptService);
    private sdDataService = inject(StandingdataDataService);
    private _translation = injectTranslation(scope);

    public isBimAdmin;

    constructor() {
        super();

        const permService = this.permService;
        this.isBimAdmin = permService.permissions.IsBimAdmin;
    }

    async moiExport() {
        await this._translation.ready();
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this._translation.t("bim.moisExportTitle"), null, fields, ExportType.Mois);
    }

    async moiSummaryExport() {
        await this._translation.ready();
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this._translation.t("bim.moiSummaryExportTitle"), null, fields, ExportType.MoiSummary);
    }

    async moiCommentsExport() {
        await this._translation.ready();
        var fields = this.getFields([
            this.fieldName((x) => x.moiTypes),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.includeDeletedandClosedmois),
        ]);

        this.showExportPopup(this._translation.t("bim.moiAndCommentsExportTitle"), null, fields, ExportType.MoiComments);
    }

    async findingReportAssessmentExport() {
        await this._translation.ready();
        var fields = this.getFields([this.fieldName((x) => x.reportTypes)]);

        this.showExportPopup(
            this._translation.t("bim.findingReportsAssessmentsTitle"),
            null,
            fields,
            ExportType.FindingReportsAndAssessments
        );
    }

    async findingReportInformationExport() {
        await this._translation.ready();
        var fields = this.getFields([
            this.fieldName((x) => x.organizations),
            this.fieldName((x) => x.reportTypes),
            this.fieldName((x) => x.findingReports),
        ]);

        this.showExportPopup(
            this._translation.t("bim.findingReportInformationTitle"),
            null,
            fields,
            ExportType.FindingReportInformation
        );
    }

    showExportPopup(
        title: string,
        message: string,
        fields: GenericListField[],
        exportype: ExportType
    ): void {
        this.cerrixPrompt
            .prompt({
                height: "500px",
                data: {
                    title: title,
                    message: message,
                    fields: fields,

                    confirmButton: {
                        text: this._translation.t("downloadButton"),
                        icon: "fad fa-cloud-download",
                    },
                },
            })
            .toPromise()
            .then((result: MoiExportConfigModel) => {
                if (!result) {
                    return;
                }

                const extraString = this.toRequestQuery(result);
                this.download(exportype, extraString);
            });
    }

    private fieldName(nameFunction: (obj: MoiExportConfigModel) => any) {
        return nameof<MoiExportConfigModel>(nameFunction);
    }

    private getFields(fieldNames: string[]): GenericListField[] {
        const requested = (field: string) => {
            return fieldNames.indexOf(field) >= 0;
        };

        var fields: GenericListField[] = [];

        if (requested(this.fieldName((x) => x.organizations))) {
            fields.push({
                prettyName: this._translation.t("organizationsField"),
                fieldName: this.fieldName((x) => x.organizations),
                fieldType: GenericListFieldType.MultiTree,
                required: true,
                selfInit: true,
                getDataMethod: () => {
                    return this.sdDataService.getOrganizations(ModuleType.BIM);
                },
            });
        }

        if (requested(this.fieldName((x) => x.moiTypes))) {
            fields.push({
                prettyName: this._translation.t("bim.moiTypesField"),
                fieldName: this.fieldName((x) => x.moiTypes),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this._translation.t("noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableMoiTypes()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.reportTypes))) {
            fields.push({
                prettyName: this._translation.t("bim.reportTypesField"),
                fieldName: this.fieldName((x) => x.reportTypes),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this._translation.t("noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableReportTypes()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.findingReports))) {
            fields.push({
                prettyName: this._translation.t("bim.findingReportsField"),
                fieldName: this.fieldName((x) => x.findingReports),
                fieldType: GenericListFieldType.MultiSelect,
                placeholder: this._translation.t("noFilter"),
                selfInit: true,
                getDataMethod: () => {
                    return this._ds
                        .getExportableFindingReports()
                        .pipe(map((x) => x.map((i) => new IdNameCombination(i.value, i.label))));
                },
            });
        }

        if (requested(this.fieldName((x) => x.includeDeletedandClosedmois))) {
            fields.push({
                prettyName: this._translation.t("bim.includeClosedMoisField"),
                fieldName: this.fieldName((x) => x.includeDeletedandClosedmois),
                fieldType: GenericListFieldType.CheckBox,
                hideFillers: true,
                selfInit: true,
            });
        }

        return fields;
    }

    private toRequestQuery(model: MoiExportConfigModel): string {
        const stringSections: string[] = [];
        if (model.includeDeletedandClosedmois) {
            stringSections.push("includedeletedandclosedmois=true");
        }

        if (model.organizations && model.organizations.length > 0) {
            stringSections.push("organizations=" + model.organizations.join(";"));
        }

        if (model.moiTypes && model.moiTypes.length > 0) {
            stringSections.push("moiTypes=" + model.moiTypes.join(";"));
        }

        if (model.reportTypes && model.reportTypes.length > 0) {
            stringSections.push("reportTypes=" + model.reportTypes.join(";"));
        }

        if (model.findingReports && model.findingReports.length > 0) {
            stringSections.push("findingReports=" + model.findingReports.join(";"));
        }

        const extraString = stringSections.length > 0 ? stringSections.join("&") : "";
        return extraString;
    }
}
